import LottieImport from 'lottie-react'
import mobileInvestmentAnimation from '../../../assets/mobile-app.json'

// lottie-react's package.json "browser" field points bundlers (esbuild dev
// pre-bundling and Rolldown's prod build alike) at its UMD build, whose own
// `export default` is the whole CJS exports object (with `.default` being the
// actual Lottie component), not the component itself. Resolve defensively so
// this works whether or not that extra wrapping layer is present.
const Lottie: typeof LottieImport =
    typeof LottieImport === 'function'
        ? LottieImport
        : (LottieImport as unknown as { default: typeof LottieImport }).default;

type Props = {
  className: string
}

// ── Inline SVG icons ──────────────────────────────────────────────────────────

const GooglePlayIcon = () => (
  <svg width='28' height='28' viewBox='0 0 24 24' className='flex-shrink-0'>
    <path d='M3.609 1.814L13.792 12 3.61 22.186C3.227 21.95 3 21.543 3 21.086V2.914C3 2.457 3.227 2.05 3.609 1.814Z' fill='#EA4335' />
    <path d='M13.792 12L3.609 1.814C3.97 1.59 4.425 1.566 4.831 1.757L16.602 8.443L13.792 12Z' fill='#FBBC04' />
    <path d='M13.792 12L16.602 15.557L4.831 22.243C4.425 22.434 3.97 22.41 3.609 22.186L13.792 12Z' fill='#34A853' />
    <path d='M13.792 12L16.602 8.443L20.579 10.729C21.14 11.051 21.14 11.949 20.579 12.271L16.602 15.557L13.792 12Z' fill='#4285F4' />
  </svg>
)

const AppleIcon = () => (
  <svg width='26' height='26' viewBox='0 0 24 24' className='flex-shrink-0'>
    <path
      d='M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z'
      fill='#fff'
    />
  </svg>
)

// ── Store button ──────────────────────────────────────────────────────────────
// Solid black badges (standard App Store / Google Play badge treatment) rather
// than the translucent glass style, so they stay legible on any card background.

interface StoreButtonProps {
  href: string
  icon: React.ReactNode
  topLine: string
  mainLine: string
}

const StoreButton = ({ href, icon, topLine, mainLine }: StoreButtonProps) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='text-decoration-none d-flex align-items-center gap-3'
    style={{
      background: '#000',
      border: '1px solid rgba(255,255,255,0.22)',
      borderRadius: '14px',
      padding: '11px 20px',
      transition: 'background 0.2s, transform 0.2s',
    }}
    onMouseEnter={e => {
      ;(e.currentTarget as HTMLAnchorElement).style.background = '#1a1a1a'
      ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)'
    }}
    onMouseLeave={e => {
      ;(e.currentTarget as HTMLAnchorElement).style.background = '#000'
      ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)'
    }}
  >
    {icon}
    <div className='text-start'>
      <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '10px', letterSpacing: '0.6px', textTransform: 'uppercase', lineHeight: 1.2 }}>
        {topLine}
      </div>
      <div style={{ color: '#fff', fontSize: '17px', fontWeight: 700, lineHeight: 1.3 }}>{mainLine}</div>
    </div>
  </a>
)

// ── Feature rows ──────────────────────────────────────────────────────────────

const FEATURES = [
  { emoji: '📄', text: 'Create & send invoices on the go' },
  { emoji: '📷', text: 'Capture receipts with your camera' },
  { emoji: '🔄', text: 'Real-time sync across all devices' },
]

// ── Component ─────────────────────────────────────────────────────────────────

const MarketingWidget = ({ className }: Props) => (
  <div
    className={`card border-0 overflow-hidden ${className}`}
    style={{ background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 55%, #0288d1 100%)' }}
  >
    <div className='card-body p-0'>
      <div className='row g-0 align-items-center'>

        {/* ── Left column: copy ── */}
        <div className='col-lg-6 p-10 p-xl-14'>

          {/* Badge */}
          <span
            className='d-inline-flex align-items-center gap-2 fw-semibold fs-8 mb-6 px-4 py-2'
            style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '50px',
              color: 'rgba(255,255,255,0.9)',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            {'📱 Available on iOS & Android'.toTranslatedDisplay()}
          </span>

          <h2 className='fw-bold text-white mb-3' style={{ fontSize: '2rem', lineHeight: 1.2 }}>
            {'myBooks on Mobile'.toTranslatedDisplay()}
          </h2>

          <p className='mb-8 fs-6' style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}>
            {'Take your bookkeeping anywhere — manage invoices, track expenses and stay in sync, all from your phone.'.toTranslatedDisplay()}
          </p>

          {/* Features */}
          <div className='d-flex flex-column gap-4 mb-10'>
            {FEATURES.map(f => (
              <div key={f.text} className='d-flex align-items-center gap-3'>
                <span
                  className='d-flex align-items-center justify-content-center flex-shrink-0'
                  style={{
                    width: '32px',
                    height: '32px',
                    background: 'rgba(255,255,255,0.18)',
                    borderRadius: '50%',
                    fontSize: '15px',
                  }}
                >
                  {f.emoji}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '15px' }}>{f.text.toTranslatedDisplay()}</span>
              </div>
            ))}
          </div>

          {/* Store buttons */}
          <div className='d-flex gap-4 flex-wrap'>
            <StoreButton
              href='https://play.google.com/store/apps/details?id=app.mybooksai.acct'
              icon={<GooglePlayIcon />}
              topLine={'Get it on'.toTranslatedDisplay()}
              mainLine={'Google Play'.toTranslatedDisplay()}
            />
            <StoreButton
              href='https://apps.apple.com/us/app/mybooksai-billing-accounting/id6748947230'
              icon={<AppleIcon />}
              topLine={'Download on the'.toTranslatedDisplay()}
              mainLine={'App Store'.toTranslatedDisplay()}
            />
          </div>
        </div>

        {/* ── Right column: animation + decorative circles ── */}
        <div
          className='col-lg-6 position-relative d-flex justify-content-center align-items-end overflow-hidden'
          style={{ minHeight: '280px' }}
        >
          {/* Outer decorative circle */}
          <div
            className='position-absolute'
            style={{
              width: '440px',
              height: '440px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
              right: '-80px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          {/* Inner decorative circle */}
          <div
            className='position-absolute'
            style={{
              width: '280px',
              height: '280px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.07)',
              right: '30px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          <Lottie
            animationData={mobileInvestmentAnimation}
            loop
            autoplay
            style={{ width: 320, height: 300, position: 'relative', zIndex: 1 }}
          />
        </div>

      </div>
    </div>
  </div>
)

export { MarketingWidget }
